import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaUserPlus,
  FaCheckCircle,
  FaTimesCircle,
  FaShieldAlt,
  FaHeartbeat,
} from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "./Navbar";
import { BASE_URL } from "../backend/backend.js";

const Signup = () => {
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(
    window.innerWidth < 900
  );

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    mobileNumber: "",
  });

  const [isLoading, setIsLoading] =
    useState(false);

  const [response, setResponse] = useState({
    type: "",
    message: "",
    visible: false,
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
    };

    window.addEventListener(
      "resize",
      handleResize
    );

    return () =>
      window.removeEventListener(
        "resize",
        handleResize
      );
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);

    const requestBody = {
      username: formData.username.trim(),
      password: formData.password,
      enabled: true,
      email: formData.email.trim(),
      mobileNumber:
        formData.mobileNumber.trim(),
    };

    try {
      const res = await fetch(
        `${BASE_URL}/api/register`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type":
              "application/json",
            Accept:
              "application/json, text/plain, */*",
          },
          body: JSON.stringify(
            requestBody
          ),
        }
      );

      const contentType =
        res.headers.get("content-type");

      let message = "";

      if (
        contentType &&
        contentType.includes(
          "application/json"
        )
      ) {
        const data = await res.json();

        message =
          data.message ||
          JSON.stringify(data);
      } else {
        message = await res.text();
      }

      if (res.ok) {
        setResponse({
          type: "success",
          message:
            message ||
            "Account created successfully!",
          visible: true,
        });

        setTimeout(() => {
          navigate("/login");
        }, 2500);
      } else {
        setResponse({
          type: "error",
          message:
            message ||
            "Registration failed.",
          visible: true,
        });
      }
    } catch (error) {
      console.error(error);

      setResponse({
        type: "error",
        message:
          "Unable to connect to server.",
        visible: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <Navbar />

      {/* BACKGROUND EFFECTS */}

      <motion.div
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 20, 0],
        }}
        transition={{
          repeat: Infinity,
          duration: 10,
        }}
        style={styles.blob1}
      />

      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, -20, 0],
        }}
        transition={{
          repeat: Infinity,
          duration: 12,
        }}
        style={styles.blob2}
      />

      <div style={styles.container}>
        <motion.div
          initial={{
            opacity: 0,
            y: 50,
            rotateX: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
            rotateX: 0,
          }}
          transition={{
            duration: 0.8,
          }}
          whileHover={{
            rotateY: 2,
            rotateX: 2,
          }}
          style={{
            ...styles.card,
            flexDirection: isMobile
              ? "column"
              : "row",
          }}
        >
          {/* LEFT SECTION */}

          <div style={styles.left}>
            <motion.div
              whileHover={{
                rotate: 360,
                scale: 1.1,
              }}
              transition={{
                duration: 0.8,
              }}
              style={styles.logo}
            >
              GM
            </motion.div>

            <h1 style={styles.title}>
              Welcome To
              <br />

              <span
                style={{
                  color: "#60a5fa",
                }}
              >
                Girraj Medicare
              </span>
            </h1>

            <p style={styles.subtitle}>
              Join India’s modern healthcare
              platform with digital medicines,
              doctors and prescriptions.
            </p>

            <div style={styles.featureBox}>
              <div style={styles.feature}>
                <FaHeartbeat />
                Smart Healthcare
              </div>

              <div style={styles.feature}>
                <FaShieldAlt />
                Secure Account
              </div>

              <div style={styles.feature}>
                <FaCheckCircle />
                Fast Medicine Delivery
              </div>
            </div>
          </div>

          {/* RIGHT SECTION */}

          <div style={styles.right}>
            <div style={styles.headingContainer}>
              <h2 style={styles.heading}>
                Create Account
              </h2>

              <p style={styles.smallText}>
                Register to continue
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              style={styles.form}
            >
              <div
                style={{
                  ...styles.grid,
                  gridTemplateColumns:
                    isMobile
                      ? "1fr"
                      : "1fr 1fr",
                }}
              >
                {/* USERNAME */}

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Full Name
                  </label>

                  <div
                    style={styles.inputWrapper}
                  >
                    <FaUser
                      style={styles.icon}
                    />

                    <input
                      type="text"
                      name="username"
                      value={
                        formData.username
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="John Doe"
                      style={styles.input}
                      required
                    />
                  </div>
                </div>

                {/* EMAIL */}

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Email
                  </label>

                  <div
                    style={styles.inputWrapper}
                  >
                    <FaEnvelope
                      style={styles.icon}
                    />

                    <input
                      type="email"
                      name="email"
                      value={
                        formData.email
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="john@gmail.com"
                      style={styles.input}
                      required
                    />
                  </div>
                </div>

                {/* MOBILE */}

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Mobile Number
                  </label>

                  <div
                    style={styles.inputWrapper}
                  >
                    <FaPhone
                      style={styles.icon}
                    />

                    <input
                      type="text"
                      name="mobileNumber"
                      value={
                        formData.mobileNumber
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="+91 9876543210"
                      style={styles.input}
                      required
                    />
                  </div>
                </div>

                {/* PASSWORD */}

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Password
                  </label>

                  <div
                    style={styles.inputWrapper}
                  >
                    <FaLock
                      style={styles.icon}
                    />

                    <input
                      type="password"
                      name="password"
                      value={
                        formData.password
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="••••••••"
                      style={styles.input}
                      required
                    />
                  </div>
                </div>
              </div>

              <motion.button
                whileHover={{
                  scale: 1.03,
                }}
                whileTap={{
                  scale: 0.97,
                }}
                type="submit"
                disabled={isLoading}
                style={styles.button}
              >
                {isLoading
                  ? "Creating Account..."
                  : "Register Now"}

                {!isLoading && (
                  <FaUserPlus
                    style={{
                      marginLeft: "10px",
                    }}
                  />
                )}
              </motion.button>
            </form>

            <div style={styles.footer}>
              Already have an account?

              <Link
                to="/login"
                style={styles.link}
              >
                Login
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      {/* RESPONSE MODAL */}

      <AnimatePresence>
        {response.visible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.overlay}
          >
            <motion.div
              initial={{
                scale: 0.7,
                opacity: 0,
              }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              exit={{
                scale: 0.7,
                opacity: 0,
              }}
              style={styles.modal}
            >
              {response.type ===
              "success" ? (
                <FaCheckCircle
                  size={60}
                  color="#10b981"
                />
              ) : (
                <FaTimesCircle
                  size={60}
                  color="#ef4444"
                />
              )}

              <h2
                style={{
                  marginTop: "15px",
                }}
              >
                {response.type ===
                "success"
                  ? "Success"
                  : "Error"}
              </h2>

              <p style={styles.modalText}>
                {response.message}
              </p>

              <button
                onClick={() =>
                  setResponse({
                    ...response,
                    visible: false,
                  })
                }
                style={styles.closeButton}
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(135deg,#eff6ff,#eef2ff,#f8fafc)",
    position: "relative",
    overflow: "hidden",
    padding:
      "110px 20px 40px 20px",
    fontFamily: "'Poppins', sans-serif",
  },

  blob1: {
    position: "absolute",
    width: "500px",
    height: "500px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle,#93c5fd,transparent)",
    top: "-100px",
    left: "-100px",
    filter: "blur(80px)",
  },

  blob2: {
    position: "absolute",
    width: "450px",
    height: "450px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle,#c4b5fd,transparent)",
    bottom: "-100px",
    right: "-100px",
    filter: "blur(80px)",
  },

  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    position: "relative",
    zIndex: 2,
  },

  card: {
    display: "flex",
    borderRadius: "35px",
    overflow: "hidden",
    background:
      "rgba(255,255,255,0.75)",
    backdropFilter: "blur(18px)",
    boxShadow:
      "0 30px 60px rgba(0,0,0,0.12)",
    border:
      "1px solid rgba(255,255,255,0.4)",
    transformStyle: "preserve-3d",
  },

  left: {
    flex: 1,
    background:
      "linear-gradient(135deg,#0f172a,#1e293b,#2563eb)",
    color: "white",
    padding: "50px 40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  logo: {
    width: "70px",
    height: "70px",
    borderRadius: "20px",
    background:
      "linear-gradient(135deg,#3b82f6,#8b5cf6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.5rem",
    fontWeight: "900",
    marginBottom: "25px",
    boxShadow:
      "0 20px 40px rgba(59,130,246,0.4)",
  },

  title: {
    fontSize: "2.8rem",
    fontWeight: "800",
    lineHeight: 1.2,
    marginBottom: "20px",
  },

  subtitle: {
    fontSize: "1rem",
    opacity: 0.85,
    lineHeight: 1.7,
    marginBottom: "35px",
  },

  featureBox: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },

  feature: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    background:
      "rgba(255,255,255,0.1)",
    padding: "14px",
    borderRadius: "16px",
    backdropFilter: "blur(10px)",
    fontWeight: "600",
  },

  right: {
    flex: 1.3,
    padding: "50px 35px",
    background: "transparent",
  },

  headingContainer: {
    marginBottom: "30px",
  },

  heading: {
    fontSize: "2.2rem",
    fontWeight: "800",
    color: "#0f172a",
    marginBottom: "6px",
  },

  smallText: {
    color: "#64748b",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },

  grid: {
    display: "grid",
    gap: "20px",
  },

  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  label: {
    fontSize: "0.9rem",
    fontWeight: "600",
    color: "#475569",
  },

  inputWrapper: {
    position: "relative",
  },

  icon: {
    position: "absolute",
    top: "50%",
    left: "16px",
    transform: "translateY(-50%)",
    color: "#94a3b8",
  },

  input: {
    width: "100%",
    padding: "15px 15px 15px 48px",
    borderRadius: "16px",
    border: "1px solid #dbeafe",
    outline: "none",
    background:
      "rgba(255,255,255,0.9)",
    fontSize: "0.95rem",
    transition: "0.3s",
    boxSizing: "border-box",
    boxShadow:
      "0 8px 20px rgba(59,130,246,0.06)",
  },

  button: {
    width: "100%",
    padding: "16px",
    borderRadius: "18px",
    border: "none",
    background:
      "linear-gradient(135deg,#2563eb,#7c3aed)",
    color: "white",
    fontWeight: "800",
    fontSize: "1rem",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow:
      "0 20px 40px rgba(37,99,235,0.35)",
  },

  footer: {
    marginTop: "25px",
    textAlign: "center",
    color: "#64748b",
    fontWeight: "500",
  },

  link: {
    marginLeft: "8px",
    color: "#2563eb",
    textDecoration: "none",
    fontWeight: "700",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background:
      "rgba(0,0,0,0.45)",
    backdropFilter: "blur(6px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2000,
    padding: "20px",
  },

  modal: {
    width: "100%",
    maxWidth: "400px",
    background: "white",
    borderRadius: "28px",
    padding: "40px 30px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxShadow:
      "0 25px 60px rgba(0,0,0,0.2)",
  },

  modalText: {
    textAlign: "center",
    color: "#64748b",
    lineHeight: 1.6,
  },

  closeButton: {
    marginTop: "25px",
    padding: "12px 28px",
    borderRadius: "14px",
    border: "none",
    background:
      "linear-gradient(135deg,#2563eb,#7c3aed)",
    color: "white",
    fontWeight: "700",
    cursor: "pointer",
  },
};

export default Signup;